import React, { useState } from "react";
import { Container, Card, Form, Button, Alert, Modal } from "react-bootstrap";
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useTranslation } from "react-i18next";

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState(null);
  const { t } = useTranslation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const auth = getAuth();

      // Set persistence to LOCAL to keep the user logged in
      await setPersistence(auth, browserLocalPersistence);

      // Sign in with email and password
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
    } catch (error) {
      console.error("Login error:", error);

      // Provide more specific error messages
      switch (error.code) {
        case "auth/user-not-found":
          setError("No admin account exists with this email.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password. Please try again.");
          break;
        case "auth/invalid-email":
          setError("Invalid email format.");
          break;
        case "auth/too-many-requests":
          setError(
            "Too many failed login attempts. Please try again later or reset your password."
          );
          break;
        default:
          setError(
            "Login failed. Please check your credentials and try again."
          );
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetSent(false);

    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch (error) {
      console.error("Password reset error:", error);

      switch (error.code) {
        case "auth/user-not-found":
          setResetError("No account exists with this email.");
          break;
        case "auth/invalid-email":
          setResetError("Invalid email format.");
          break;
        default:
          setResetError(
            "Failed to send password reset email. Please try again."
          );
      }
    }
  };

  const openResetModal = () => {
    setResetEmail(email); // Pre-fill with login email
    setResetSent(false);
    setResetError(null);
    setShowResetModal(true);
  };

  return (
    <Container
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "80vh" }}
    >
      <Card style={{ width: "400px" }}>
        <Card.Header as="h4" className="text-center bg-primary text-white">
          {t("AdminLoginTitle")}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleLogin} autoComplete="off" noValidate>
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label>{t("Email")}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder={t("Email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBasicPassword">
              <Form.Label>{t("Password")}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                placeholder={t("Password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? t("SigningIn") : t("SignIn")}
              </Button>
            </div>

            <div className="text-center mt-3">
              <Button variant="link" onClick={openResetModal} size="sm">
                {t("ForgotPassword")}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Password Reset Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ResetPassword")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetSent ? (
            <Alert variant="success">{t("ResetSent")}</Alert>
          ) : (
            <Form onSubmit={handlePasswordReset} autoComplete="off" noValidate>
              {resetError && <Alert variant="danger">{resetError}</Alert>}
              <Form.Group className="mb-3" controlId="formResetEmail">
                <Form.Label>{t("Email")}</Form.Label>
                <Form.Control
                  type="email"
                  name="resetEmail"
                  placeholder={t("EnterAdminEmail")}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100">
                {t("SendResetLink")}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminLogin;
